import { BEIGE_700, BLUE_600, BLUE_700, CORAL_400, GRAY_100, GRAY_300, GRAY_600, GRAY_700, GRAY_800, GRAY_900, MOBILE_VIEW_HEIGHT_QUERY, MOBILE_VIEW_WIDTH_QUERY, TABLET_VIEW_QUERY } from 'constants/styling/theme'
import { CircularProgress, Stack, Typography } from '@mui/material'
import { DownloadDropdownIcon, DownloadDropdownItem } from './DownloadDropdownIcon'
import { FC, ReactNode, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import AutoFixHighOutlinedIcon from '@mui/icons-material/AutoFixHighOutlined'
import BackupOutlinedIcon from '@mui/icons-material/BackupOutlined'
import { BorderBoxWrapper } from 'components/common/BorderBoxWrapper'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import { Color } from 'constants/assets'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditIcon from '@mui/icons-material/Edit'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { EditableFilename } from './EditableFilename'
import { MUIBadge } from 'components/common/MUIBadge'
import { Nullable } from 'models/helpers'
import { PhotoStudioUnsupportedModal } from 'components/common/Modals/PhotoStudioUnsupportedModal/PhotoStudioUnsupportedModal'
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import StarIcon from '@mui/icons-material/Star'
import { VisualClientDTO } from 'models/visuals'
import { VisualStatus } from 'constants/visual'
import classnames from 'classnames'
import styles from './GalleryImage.module.sass'
import useMediaQuery from '@mui/material/useMediaQuery'

/**
 * @interface Props
 */
interface Props {
  isLoading?: boolean
  /** Label of the image badge */
  label?: string
  /** Filename of the image. It's what BKBN assigned to the image. */
  filename?: string
  /** Original filename of the image. It's the image filename before any modifications. */
  originalFilename?: string
  /** The image to display */
  imageUrl?: string
  /** Error message of the image. Pass a string for backend text — it is escaped; pass an element for translated markup. */
  errorMessage?: ReactNode
  /** Whether the image has error or not */
  isError?: boolean
  /** Whether the gallery image is selected */
  isSelected?: boolean
  /** Whether the gallery image is selectable or not */
  isSelectable?: boolean
  /** Whether the gallery image show select button when hover on image */
  isHoverSelectable?: boolean
  /** Whether the selection toggle button is disabled or not */
  isSelectionDisabled?: boolean
  /** Whether the gallery image is editable with Pintura Modal */
  isEditable?: boolean
  /** Whether the gallery image can be marked as favorite or not */
  isFavoriteSelectable?: boolean
  /** Whether the gallery image is marked as favorite or not */
  isFavorite?: boolean
  /** Whether the favorite button has effect when hover on it */
  isFavoriteHoverActive?: boolean
  /** Whether the favorite button has disabled clicking */
  isFavoriteDisabled?: boolean
  /** Whether the gallery image show download button when hover on image or not */
  isDownloadable?: boolean
  /** Whether the gallery image has a delete button */
  isDeletable?: boolean
  /** Whether the gallery image is self-uploaded */
  isSelfUpload?: boolean
  /** Whether the gallery image can be renamed */
  isRenameAvailable?: boolean
  /** Custom component to display in the image box */
  children?: ReactNode
  /** Metadata of a visual */
  visualMetadata?: Nullable<VisualClientDTO>
  /** Status of the visual */
  status?: Nullable<VisualStatus>
  /** Slot for custom icons on the bottom of the image */
  iconsBottomSlot?: ReactNode
  /** Slot for badges shown on their own row, above the filename label */
  labelTopSlot?: ReactNode
  /** OnSelect action to be called */
  onSelect?: (e: React.MouseEvent) => void
  /** OnFavSelect action to be called */
  onFavSelect?: (e: React.MouseEvent) => void
  /** OnClick action to be called */
  onClick?: (e: React.MouseEvent) => void
  /** OnDelete action to be called */
  onDelete?: (e: React.MouseEvent) => void
  /** Callback for clicking on the floor planner link */
  onFloorPlannerLinkClick?: (e: React.MouseEvent) => void
  /** Callback to open Photo Studio */
  onOpenPhotoStudio?: () => void
  /** Callback for clicking on one of the drowndown options */
  onDownloadOptionClick?: (itemKey: DownloadDropdownItem) => void
}

/**
 * Gallery image component
 * 
 * @example <GalleryImage />
 */
export const GalleryImage: FC<Props> = ({
  isLoading = false,
  label,
  filename,
  originalFilename,
  imageUrl,
  errorMessage,
  isError,
  isSelected,
  isSelectable = false,
  isHoverSelectable = false,
  isFavoriteSelectable = false,
  isFavorite = false,
  isFavoriteHoverActive,
  isFavoriteDisabled = false,
  isEditable = false,
  isDownloadable = false,
  isDeletable = false,
  isSelfUpload = false,
  isRenameAvailable = false,
  children,
  isSelectionDisabled,
  visualMetadata,
  status = null,
  iconsBottomSlot,
  labelTopSlot,
  onSelect,
  onFavSelect,
  onClick,
  onDelete,
  onFloorPlannerLinkClick,
  onOpenPhotoStudio,
  onDownloadOptionClick,
}) => {
  const { t } = useTranslation(['gallery', 'photo_studio_editing', 'common'])

  const isMobileViewWidth = useMediaQuery(MOBILE_VIEW_WIDTH_QUERY)
  const isMobileViewHeight = useMediaQuery(MOBILE_VIEW_HEIGHT_QUERY)
  const isTabletView = useMediaQuery(TABLET_VIEW_QUERY)
  const isSelfEditedByClient = visualMetadata?.selfEdited
  const isAiEnhanced = visualMetadata?.isAiEnhanced

  let style: React.CSSProperties = {}

  if (imageUrl) {
    style.backgroundImage = `url('${imageUrl}')`
  }

  const onFavoriteClick = (e: React.MouseEvent<Element, MouseEvent>) => {
    e.stopPropagation()

    if (!isFavoriteDisabled) {
      onFavSelect?.(e)
    }
  }

  const floorPlannerLink = useMemo(() => {
    return visualMetadata?.floorPlanSelfEditUrl
  }, [visualMetadata])

  const isProcessingImage = useMemo(() => status === VisualStatus.PROCESSING, [status])

  const [isPhotoStudioUnsupportedModalOpen, setIsPhotoStudioUnsupportedModalOpen] = useState(false)

  const handlePhotoStudioClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!isProcessingImage) {
      if ((isMobileViewWidth && isMobileViewHeight) || isTabletView) {
        setIsPhotoStudioUnsupportedModalOpen(true)
      } else {
        onOpenPhotoStudio?.()
      }
    }
  }

  return (
    <Stack gap=".9rem">

      <div
        className={classnames(styles.galleryImage,
          {
            [styles.selected]: isSelected,
            [styles.hoverSelectButton]: isHoverSelectable,
            [styles.hoverFavoriteButton]: isFavoriteHoverActive,
            [styles.hoverEditButton]: !!floorPlannerLink,
            [styles.hoverEditVisualButton]: isEditable,
            [styles.hoverDownloadableButton]: isDownloadable,
            [styles.clickable]: !!onClick,
            [styles.error]: isError,
            [styles.mobile]: (isMobileViewWidth && isMobileViewHeight) || isTabletView,
            [styles.processing]: isProcessingImage,
          })
        }
        style={style}
        onClick={(e) => !isProcessingImage && onClick?.(e)}
      >

        {isLoading &&
          <CircularProgress
            variant="indeterminate"
            size={30}
            thickness={5}
            sx={{
              color: Color.GRAY_TEXT,
              borderRadius: '50%',
              // Background ring for unfilled progress
              boxShadow: `inset 0 0 0px 3px ${BEIGE_700}`
            }}
          />
        }

        {/** TOP ICONS */}
        <Stack direction="row" gap={0.8} position="absolute" sx={{ top: '0.8rem', left: 0, right: 0, padding: '0 0.8rem' }}>

          {isSelectable &&
            <span
              className={classnames(styles.iconButton, styles.selectButton, { [styles.selected]: isSelected, [styles.disabled]: isSelectionDisabled })}
              onClick={e => {
                e.stopPropagation()
                if (!isSelectionDisabled && !isProcessingImage) onSelect?.(e)
              }}>
              <CheckRoundedIcon className={styles.checkIcon} sx={{ color: BLUE_600 }} />
            </span>
          }

          <Stack direction="row" gap={0.8} sx={{ marginLeft: 'auto' }}>

            {isEditable &&
              <span
                className={classnames(styles.iconButton, styles.editVisualButton)}
                title={t('photo_studio_editing:title')}
                onClick={handlePhotoStudioClick}
              >
                <EditOutlinedIcon sx={{ color: GRAY_800, fontSize: '1.6rem' }} />
                <Typography variant="text-sm" fontWeight={500} color={GRAY_800}>
                  {t('common:Edit')}
                </Typography>
              </span>
            }

            {isDownloadable &&
              <span className={classnames(styles.iconButton, styles.downloadButton)} onClick={(e) => e.stopPropagation()}>
                <DownloadDropdownIcon
                  onDownloadItemClick={onDownloadOptionClick}
                />
              </span>
            }

            {isFavoriteSelectable &&
              <span
                className={classnames(styles.iconButton, styles.favButton, { [styles.favSelected]: isFavorite, [styles.favDisabled]: isFavoriteDisabled })}
                onClick={e => {
                  e.stopPropagation()
                  if (!isFavoriteDisabled && !isProcessingImage) onFavoriteClick(e)
                }}
              >
                {isFavorite ?
                  <StarIcon sx={{ color: BLUE_600, fontSize: '1.6rem' }} />
                  :
                  <StarBorderIcon sx={{ color: isFavoriteDisabled ? GRAY_600 : BLUE_600, fontSize: '1.6rem' }} />
                }
              </span>
            }

            {!!floorPlannerLink &&
              <a
                className={classnames(styles.iconButton, styles.editButton)}
                onClick={e => onFloorPlannerLinkClick?.(e)}
                href={floorPlannerLink}
                target="_blank" rel="noreferrer"
                title={t('edit_floor_plan')}
              >
                <EditIcon sx={{ color: BLUE_600, fontSize: '1.6rem' }} />
              </a>
            }

            {isDeletable &&
              <span
                className={classnames(styles.iconButton, styles.deleteButton)}
                onClick={e => {
                  e.stopPropagation()
                  onDelete?.(e)
                }}>
                <DeleteOutlineIcon sx={{ color: CORAL_400, fontSize: '1.6rem' }} />
              </span>
            }

          </Stack>

        </Stack>

        {/** ERROR - Full container size */}
        {/* TODO: New error message for load/upload/download image will be in another task, can handle it like <ErrorMessage /> component. */}
        {isError && !!errorMessage && <p className={styles.errorMsg}>{errorMessage}</p>}

        {/** BOTTOM CONTENT */}
        <Stack
          gap={.6}
          left="0"
          right="0"
          width="100%"
          bottom=".8rem"
          padding="0 0.8rem"
          position="absolute"
          boxSizing="border-box"
        >

          {/** Badges above the filename */}
          {labelTopSlot &&
            <Stack className={styles.labelBadge} direction="row" gap={0.6} alignItems="center" maxWidth="100%">
              {labelTopSlot}
            </Stack>
          }

          {/** Processing image state */}
          <Stack
            gap={.6}
            direction="row"
            alignItems="center"
            justifyContent="flex-start"
          >

            {!!label &&
              <MUIBadge className={styles.labelBadge} label={label} size="sm" color="gray" sx={{ flex: '0 1 auto', maxWidth: 'calc(70% - 4rem)', textOverflow: 'ellipsis' }} />
            }

            {isProcessingImage &&
              <BorderBoxWrapper
                borderColor={GRAY_300}
                backgroundColor={GRAY_100}
                sx={{
                  opacity: 1,
                  gap: '0.4rem',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0.2rem 0.6rem',
                  boxSizing: 'border-box',
                }}
              >

                <CircularProgress size={14} thickness={4} sx={{ color: BLUE_700 }} className={styles.processingTag} />

                <Typography variant="text-xs" fontWeight={500} color={GRAY_700}>
                  {t('image_processing')}
                </Typography>

              </BorderBoxWrapper>
            }

            {/** Custom icons */}
            {iconsBottomSlot &&
              <Stack className={styles.iconsBottomSlot} direction="row" gap={0.6} alignItems="center" marginLeft="auto">
                {iconsBottomSlot}
              </Stack>
            }

          </Stack>

        </Stack>

        {children}

        <PhotoStudioUnsupportedModal
          isOpen={isPhotoStudioUnsupportedModalOpen}
          onClose={() => setIsPhotoStudioUnsupportedModalOpen(false)}
        />

      </div>

      <Stack flexDirection="row" gap=".8rem" justifyContent="space-between" alignItems="center" boxSizing="border-box">

        {!!label &&
          <>
            {isRenameAvailable ?
              <EditableFilename label={originalFilename || label} filename={filename} />
              :
              <Typography variant="text-sm" color={GRAY_900} fontWeight={450} textOverflow="ellipsis" minWidth={0} flex="1 1 auto" overflow="hidden" whiteSpace="nowrap" title={label}>
                {label}
              </Typography>
            }

            {/* Whether Client used Pintura to self edit visual or image was AI enhanced */}
            <Stack flexDirection="row" gap={.4} flexShrink={0}>

              {(isSelfEditedByClient || isAiEnhanced) &&
                <MUIBadge size="sm" color="darkGray" sx={{ gap: 0, width: '2.8rem' }} icon={<AutoFixHighOutlinedIcon />} />
              }

              {isSelfUpload &&
                <MUIBadge size="sm" color="darkGray" sx={{ gap: 0, width: '2.8rem' }} icon={<BackupOutlinedIcon />} />
              }

              {(isDownloadable && !isSelfUpload) &&
                <MUIBadge size="sm" color="darkGray" sx={{ gap: 0, width: '2.8rem' }} icon={<ShoppingCartOutlinedIcon />} />
              }

            </Stack>
          </>
        }

      </Stack>

    </Stack>
  )
}
